/**
 * Session Participant Groups Store - session-level group participation (session_participant_groups).
 */

import { create } from 'zustand';
import { getSafeSupabaseClient, generateUUID } from '../../lib/supabaseSync';
import type { ParticipantRole } from './cycleParticipantsStore';

export type SessionParticipantGroupStatus = 'active' | 'removed';

export interface SessionParticipantGroup {
  id: string;
  sessionId: string;
  groupId: string;
  role: ParticipantRole;
  status: SessionParticipantGroupStatus;
  addedBy: string;
  addedAt: Date;
}

interface SessionParticipantGroupsState {
  participantGroups: SessionParticipantGroup[];
  isLoading: boolean;
  error: string | null;
  fetchParticipantGroupsForSession: (sessionId: string) => Promise<void>;
  addParticipantGroup: (input: { sessionId: string; groupId: string; role: ParticipantRole; addedBy: string }) => Promise<SessionParticipantGroup | null>;
  removeParticipantGroup: (id: string) => Promise<boolean>;
  getParticipantGroupsForSession: (sessionId: string) => SessionParticipantGroup[];
  getActiveParticipantGroupsForSession: (sessionId: string) => SessionParticipantGroup[];
}

const TABLE_NAME = 'session_participant_groups';

function rowToParticipantGroup(row: Record<string, unknown>): SessionParticipantGroup {
  return {
    id: row.id as string,
    sessionId: row.session_id as string,
    groupId: row.group_id as string,
    role: row.role as ParticipantRole,
    status: (row.status as SessionParticipantGroupStatus) || 'active',
    addedBy: row.added_by as string,
    addedAt: row.added_at != null ? new Date(row.added_at as string) : new Date(0),
  };
}

function participantGroupToRow(p: Partial<SessionParticipantGroup>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (p.id != null) out.id = p.id;
  if (p.sessionId != null) out.session_id = p.sessionId;
  if (p.groupId != null) out.group_id = p.groupId;
  if (p.role != null) out.role = p.role;
  if (p.status != null) out.status = p.status;
  if (p.addedBy != null) out.added_by = p.addedBy;
  if (p.addedAt != null) out.added_at = p.addedAt.toISOString();
  return out;
}

export const useSessionParticipantGroupsStore = create<SessionParticipantGroupsState>()((set, get) => ({
  participantGroups: [],
  isLoading: false,
  error: null,

  fetchParticipantGroupsForSession: async (sessionId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select('*')
        .eq('session_id', sessionId)
        .order('added_at', { ascending: true });
      if (error) {
        if (error.code === '42P01' || error.message?.toLowerCase().includes('does not exist')) {
          set(state => {
            const other = state.participantGroups.filter(g => g.sessionId !== sessionId);
            return { participantGroups: other, isLoading: false, error: null };
          });
          return;
        }
        set({ isLoading: false, error: error.message });
        return;
      }
      const list = (data || []).map(rowToParticipantGroup);
      set(state => {
        const other = state.participantGroups.filter(g => g.sessionId !== sessionId);
        return { participantGroups: [...other, ...list], isLoading: false, error: null };
      });
    } catch (err) {
      set({ isLoading: false, error: String(err) });
    }
  },

  addParticipantGroup: async (input) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return null;
    try {
      const { data: existing } = await supabase
        .from(TABLE_NAME)
        .select('id, status')
        .eq('session_id', input.sessionId)
        .eq('group_id', input.groupId)
        .maybeSingle();
      if (existing) {
        if (existing.status === 'removed') {
          const { data: updated, error: upErr } = await supabase
            .from(TABLE_NAME)
            .update({ status: 'active' })
            .eq('id', existing.id)
            .select()
            .single();
          if (upErr) {
            set({ error: upErr.message });
            return null;
          }
          const g = rowToParticipantGroup(updated);
          set(state => ({
            participantGroups: state.participantGroups.map(x => (x.id === g.id ? g : x)),
            error: null,
          }));
          return g;
        }
        return rowToParticipantGroup(existing as unknown as Record<string, unknown>);
      }
      const newRow: SessionParticipantGroup = {
        id: generateUUID(),
        sessionId: input.sessionId,
        groupId: input.groupId,
        role: input.role,
        status: 'active',
        addedBy: input.addedBy,
        addedAt: new Date(),
      };
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .insert(participantGroupToRow(newRow))
        .select()
        .single();
      if (error) {
        set({ error: error.message });
        return null;
      }
      const inserted = rowToParticipantGroup(data as unknown as Record<string, unknown>);
      set(state => ({ participantGroups: [...state.participantGroups, inserted], error: null }));
      return inserted;
    } catch (err) {
      set({ error: String(err) });
      return null;
    }
  },

  removeParticipantGroup: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;
    const g = get().participantGroups.find(x => x.id === id);
    if (!g) return false;
    try {
      const { error } = await supabase
        .from(TABLE_NAME)
        .update({ status: 'removed' })
        .eq('id', id);
      if (error) {
        set({ error: error.message });
        return false;
      }
      set(state => ({
        participantGroups: state.participantGroups.map(x =>
          x.id === id ? { ...x, status: 'removed' as SessionParticipantGroupStatus } : x
        ),
        error: null,
      }));
      return true;
    } catch (err) {
      set({ error: String(err) });
      return false;
    }
  },

  getParticipantGroupsForSession: (sessionId) =>
    get().participantGroups.filter(g => g.sessionId === sessionId),
  getActiveParticipantGroupsForSession: (sessionId) =>
    get().participantGroups.filter(g => g.sessionId === sessionId && g.status === 'active'),
}));
